import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { EngineDocumentResponseExpanded } from '@engine/models/document'

import { adminFetch } from '#api/client.js'
import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { CopyButton } from '#components/CopyButton.js'
import { CopyRequestButton } from '#components/CopyRequestButton.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { DocumentDetails } from './DocumentDetails.js'
import { DocumentsList } from './DocumentsList.js'

const DocumentsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedDocument, setSelectedDocument] =
    useState<EngineDocumentResponseExpanded | null>(null)

  const [trackingNumber, setTrackingNumber] = useState(
    searchParams.get('trackingNumber') || '',
  )
  const [externalId, setExternalId] = useState(
    searchParams.get('externalId') || '',
  )
  const [documentId, setDocumentId] = useState(
    searchParams.get('documentId') || '',
  )
  const [status, setStatus] = useState(searchParams.get('status') || '')
  const [pickupDateFrom, setPickupDateFrom] = useState(
    searchParams.get('pickupDateFrom') || '',
  )
  const [pickupDateTo, setPickupDateTo] = useState(
    searchParams.get('pickupDateTo') || '',
  )
  const [createdAtFrom, setCreatedAtFrom] = useState(
    searchParams.get('createdAtFrom') || '',
  )
  const [createdAtTo, setCreatedAtTo] = useState(
    searchParams.get('createdAtTo') || '',
  )
  const [sortBy, setSortBy] = useState(
    searchParams.get('sortBy') || '-createdAt',
  )
  const [scac, setScac] = useState(searchParams.get('scac') || '')

  const {
    items: documents,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<EngineDocumentResponseExpanded>('documents', {
    trackingNumber,
    externalId,
    documentId,
    status,
    pickupDateFrom,
    pickupDateTo,
    createdAtFrom,
    createdAtTo,
    scac,
    sort: sortBy,
    limit: 50,
  })

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  useEffect(() => {
    const params = new URLSearchParams()
    if (trackingNumber) params.set('trackingNumber', trackingNumber)
    if (externalId) params.set('externalId', externalId)
    if (documentId) params.set('documentId', documentId)
    if (status) params.set('status', status)
    if (pickupDateFrom) params.set('pickupDateFrom', pickupDateFrom)
    if (pickupDateTo) params.set('pickupDateTo', pickupDateTo)
    if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
    if (createdAtTo) params.set('createdAtTo', createdAtTo)
    if (sortBy !== '-createdAt') params.set('sortBy', sortBy)
    if (scac) params.set('scac', scac)
    setSearchParams(params, { replace: true })
  }, [
    trackingNumber,
    externalId,
    documentId,
    status,
    pickupDateFrom,
    pickupDateTo,
    createdAtFrom,
    createdAtTo,
    sortBy,
    scac,
    setSearchParams,
  ])

  const handleDocumentSelect = useCallback(
    (document: EngineDocumentResponseExpanded) => {
      setSelectedDocument(document)
    },
    [],
  )

  const handleCloseDetails = useCallback(() => {
    setSelectedDocument(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Document Details</span>
      {selectedDocument && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?documentId=${selectedDocument._id}`}
          />
          <CopyRequestButton
            label="Copy Document Request"
            getContent={() =>
              adminFetch(`documents/${selectedDocument._id}/service-request`)
            }
          />
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Document Subscriptions"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No document subscriptions found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <FilterInput
                label="Tracking Number"
                value={trackingNumber}
                onChange={setTrackingNumber}
                placeholder="Search by tracking number..."
              />
              <FilterInput
                label="External ID"
                value={externalId}
                onChange={setExternalId}
                placeholder="Search by external ID..."
              />
              <FilterInput
                label="Document ID"
                value={documentId}
                onChange={setDocumentId}
                placeholder="Search by document ID..."
              />
            </FilterRow>

            <FilterRow>
              <FilterSelect
                label="Status"
                value={status}
                onChange={setStatus}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'ok', label: 'OK' },
                  { value: 'not-found', label: 'Not Found' },
                  { value: 'error', label: 'Error' },
                ]}
              />
              <ScacTypeahead value={scac} onChange={value => setScac(value)} />
              <FilterInput
                label="Pickup Date From"
                value={pickupDateFrom}
                onChange={setPickupDateFrom}
                type="date"
              />
              <FilterInput
                label="Pickup Date To"
                value={pickupDateTo}
                onChange={setPickupDateTo}
                type="date"
              />
            </FilterRow>

            <FilterRow>
              <FilterInput
                label="Created Date From"
                value={createdAtFrom}
                onChange={setCreatedAtFrom}
                type="date"
              />
              <FilterInput
                label="Created Date To"
                value={createdAtTo}
                onChange={setCreatedAtTo}
                type="date"
              />
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          documents.length > 0 ? (
            <DocumentsList
              documents={documents}
              selectedId={selectedDocument?._id}
              onSelect={handleDocumentSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
              sortBy={sortBy}
              onSortChange={setSortBy}
              onUpdatesTriggered={refresh}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedDocument}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <DocumentDetails document={selectedDocument} />
      </Slider>
    </>
  )
}

export { DocumentsView }
